import { useCallback, useEffect, useState } from 'react';
import { matchPath } from 'react-router-dom';
import clsx from 'clsx';
import { isEmpty } from 'lodash-es';
import { AText, Box, Button, Search } from '@alf/uikit';
import { LOG_ACTIONS, useLogAmplitude } from '@namespace/analytics';
import { useGeneralRoutes, useOtherSettings } from '@namespace/cms';
import { useDeviceStore } from '@namespace/device';
import {
  scrollTo,
  SESSION_STORAGE_KEYS,
  setSessionStorageValue
} from '@namespace/helpers';
import { useI18n, useLocation } from '@namespace/i18n';
import { useAddSearchParams } from '@namespace/router';
import useSearchParams from '@namespace/router/src/hooks/useSearchParams';
import { CasinoSearchBackHeader } from '../../common/CasinoSearchBackHeader';
import {
  IS_USE_PLEX_API,
  LOBBY_GAMES_SECTION_COUNT,
  MIN_GAMES_SEARCH_QUERY_LENGTH,
  PLEX_MOBILE_SEARCH_MAX_GAMES_PAGES_LIMIT
} from '../../constants';
import { GamesControlsContext } from '../../context';
import {
  useCasinoSearchLogAction,
  useGamesLiveData,
  useGetExcludedWagerGamesIdList,
  useGetFeedName,
  useGetLobbyPageUrl,
  useLoadCasinoFeed,
  useLoadFavoritesList,
  useScrollToPrevGamesPosition,
  useSearchFilter,
  useSearchGames
} from '../../hooks';
import { useLoadFeedProviders } from '../../hooks/useLoadFeedProviders';
import { useLoadRecommendedGames } from '../../hooks/useLoadRecommendedGames';
import { useLoadWagerSlotsGames } from '../../hooks/useLoadWagerSlotsGames';
import { GamesFiltersSection } from './components/Mobile';
import BottomCategoryGamesList from './components/Shared/BottomCategoryGamesList';
import { getGamesListId } from './utils/getGamesListId';
import { SearchResultGames } from './SearchResultGames';
import { useCasinoGamesSearchPagination } from './useCasinoGamesSearchPagination';
import { useGameListCommonProps } from './useGameListCommonProps';
import { useRecommendedGamesCasinoSearch } from './useRecommendedGamesCasinoSearch';

import styles from './index.module.css';

const CasinoGamesSearch = () => {
  const { t } = useI18n();
  const logAmplitude = useLogAmplitude();
  const gameListCommonProps = useGameListCommonProps();
  const { casinoType, controls } = gameListCommonProps;

  const feedName = useGetFeedName(casinoType);
  const { query: searchParamsQuery } = useSearchParams();
  const lobbyPageUrl = useGetLobbyPageUrl(casinoType);
  useLoadRecommendedGames(casinoType);
  useLoadWagerSlotsGames(feedName, LOBBY_GAMES_SECTION_COUNT);

  const recommendedCategory = useRecommendedGamesCasinoSearch();

  const { isLoaded: isCategoriesLoaded } = useLoadCasinoFeed({
    feedNames: feedName,
    casinoType,
    limit: LOBBY_GAMES_SECTION_COUNT
  });

  useLoadFeedProviders({
    feedName,
    casinoType,
    limit: 1
  });
  const isFeedLoaded = isCategoriesLoaded;
  useGamesLiveData(casinoType, feedName);
  useGetExcludedWagerGamesIdList();

  const {
    gamesFilterConfig = {},
    casinoSearchBottomCategory = {},
    isGameCardsMinimisedConfig = false
  } = useOtherSettings();
  const { isDesktop } = useDeviceStore();

  const {
    isCasinoSearchWithCategoriesFilter = false,
    isCasinoSearchWithProvidersFilter = false
  } = gamesFilterConfig[casinoType] ?? {};
  const isCasinoSearchWithFilter =
    isCasinoSearchWithCategoriesFilter || isCasinoSearchWithProvidersFilter;
  const [query, setQuery] = useState(searchParamsQuery || '');
  const { pathname } = useLocation();
  const addSearchParams = useAddSearchParams();

  const { gamesSearch } = useGeneralRoutes();

  useLoadFavoritesList(casinoType, feedName);

  const { page, handlePageChange } = useCasinoGamesSearchPagination(true);

  const filterData = useSearchFilter(handlePageChange);

  const { resetAllFilters, filterState } = filterData;

  const {
    gamesResult,
    debouncedQuery,
    isLoading: isPlexLoading,
    loadGames,
    isAllGamesLoaded,
    totalGames
  } = useSearchGames({
    feedName,
    query,
    pageSize: LOBBY_GAMES_SECTION_COUNT,
    filterState,
    initialPageFromUrl: true
  });

  const gamesListId = getGamesListId(
    debouncedQuery,
    filterState,
    recommendedCategory
  );

  const isCurrentSearchPath = !isEmpty(
    matchPath({ path: `/:service${gamesSearch}`, end: true }, pathname)
  );

  useEffect(() => {
    if (!isCurrentSearchPath) {
      return;
    }

    // if initial page is more than maxPage(8 for favbet) - reset autoscroll
    if (
      IS_USE_PLEX_API &&
      page > PLEX_MOBILE_SEARCH_MAX_GAMES_PAGES_LIMIT &&
      !gamesResult?.length
    ) {
      setSessionStorageValue(
        SESSION_STORAGE_KEYS.CASINO_PREV_PAGE_SCROLL_TOP,
        0
      );
    }

    // if initial page is more than maxPage(8 for favbet) - reset page to 1st
    // Plex cant load more than 200 games at once
    addSearchParams({
      query: debouncedQuery,
      ...(IS_USE_PLEX_API &&
      page > PLEX_MOBILE_SEARCH_MAX_GAMES_PAGES_LIMIT &&
      !gamesResult?.length
        ? { page: 1 }
        : {})
    });
  }, [debouncedQuery, isCurrentSearchPath]);

  useEffect(() => {
    if (debouncedQuery?.length >= MIN_GAMES_SEARCH_QUERY_LENGTH) {
      logAmplitude({
        actionName: LOG_ACTIONS.SEARCH_INPUT,
        isMatchPath: true
      });
    }
  }, [debouncedQuery]);

  const isFilterFilled = !!(
    filterState.categories.length || filterState.providers.length
  );

  const isScrollToPrevPositionEnabled = !!(
    isFeedLoaded &&
    !isFilterFilled &&
    !isPlexLoading &&
    (!debouncedQuery || gamesResult?.length)
  );

  useEffect(() => {
    // need manually scroll to top, because "/casino/:category" route used in routesWithDisabledAutoScroll
    // and casino/search matches this route. maybe need to use another route in this case

    if (!searchParamsQuery) {
      scrollTo({ timeout: 200 });
    }
  }, []);

  useScrollToPrevGamesPosition(
    gamesListId,
    isScrollToPrevPositionEnabled,
    casinoType
  );

  const { handleGoBackClick, handleLaunchGameClick } = useCasinoSearchLogAction(
    query,
    casinoType,
    gamesResult?.length,
    filterState
  );

  const onSearchQueryChange = useCallback(
    (newValue) => {
      setQuery(newValue);
      handlePageChange(1);
    },
    [setQuery, handlePageChange]
  );

  const onChangePage = useCallback(
    (newPage) => {
      if (!isFilterFilled) {
        handlePageChange(newPage);
      }
      loadGames();
    },
    [isFilterFilled, handlePageChange]
  );

  const isSearchQueryFilled =
    debouncedQuery.length >= MIN_GAMES_SEARCH_QUERY_LENGTH;

  return (
    <GamesControlsContext.Provider value={controls}>
      <CasinoSearchBackHeader
        lobbyUrl={lobbyPageUrl}
        onGoBack={handleGoBackClick}
      />
      <Search
        setQuery={onSearchQueryChange}
        query={query}
        minQueryLength={MIN_GAMES_SEARCH_QUERY_LENGTH}
        isAutofocus={!searchParamsQuery}
        usePopover={false}
        haveToCloseOutsideClick={false}
        placeholder={t('casinoGames.searchplaceholder')}
      />

      {isCasinoSearchWithFilter && (
        <GamesFiltersSection
          feedName={feedName}
          isFeedLoaded={isFeedLoaded}
          filterData={filterData}
          withCategoriesFilter={isCasinoSearchWithCategoriesFilter}
          withProvidersFilter={isCasinoSearchWithProvidersFilter}
        />
      )}

      {/* Search info block should not be shown when plex is active
        plex doesnt have multifiltr functionality yet, plex doesnt provide totalGames (found games)
        */}
      {!IS_USE_PLEX_API && (isFilterFilled || isSearchQueryFilled) ? (
        <Box
          className={clsx([
            styles.foundTitleSection,
            isGameCardsMinimisedConfig &&
              !isDesktop &&
              styles.foundTitleSectionMinimised
          ])}
        >
          <AText breed="BU" size="l" color="txt_color_3">
            {`${t('web.games.search.found')}: ${totalGames || 0} ${t(
              'web.games.search.found.games'
            )}`}
          </AText>
          {isFilterFilled && (
            <Button
              intent="quaternary"
              onClick={resetAllFilters}
              className={styles.resetButton}
            >
              <AText breed="BU" size="l">
                {t('web.games.search.filters.reset')}
              </AText>
            </Button>
          )}
        </Box>
      ) : null}

      {(isFilterFilled || isSearchQueryFilled) && (
        <SearchResultGames
          isFeedLoaded={isFeedLoaded}
          games={gamesResult}
          gamesListId={gamesListId}
          onGameClick={handleLaunchGameClick}
          page={page}
          onChangePage={onChangePage}
          {...gameListCommonProps}
          isPlexAllGamesLoaded={isAllGamesLoaded}
          isPlexLoading={isPlexLoading}
        />
      )}
      {!gamesResult?.length && (
        <BottomCategoryGamesList
          casinoSearchBottomCategory={casinoSearchBottomCategory}
          feedName={feedName}
          casinoType={casinoType}
          isGamesResultEmpty={!gamesResult?.length}
          recommendedCategory={recommendedCategory}
        />
      )}
    </GamesControlsContext.Provider>
  );
};

export default CasinoGamesSearch;
